import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { apiPost } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { SessionUser, RegisterRequest } from '@tinyrewards/shared';

function getPasswordStrength(password: string): { score: number; label: string; color: 'error' | 'warning' | 'info' | 'success' } {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    if (score <= 1) return { score: 25, label: 'Weak', color: 'error' };
    if (score === 2) return { score: 50, label: 'Fair', color: 'warning' };
    if (score === 3) return { score: 75, label: 'Good', color: 'info' };
    return { score: 100, label: 'Strong', color: 'success' };
}

export default function RegisterPage() {
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [organizationName, setOrganizationName] = useState('');
    const [errors, setErrors] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);

    const strength = password.length > 0 ? getPasswordStrength(password) : null;
    const confirmMismatch = confirmPassword.length > 0 && confirmPassword !== password;

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setErrors(['Passwords do not match']);
            return;
        }
        setLoading(true);
        setErrors([]);

        const data: RegisterRequest = {
            firstName,
            lastName,
            email,
            password,
            ...(organizationName.trim() ? { organizationName: organizationName.trim() } : {}),
        };

        const res = await apiPost<SessionUser>('/auth/register', data);
        setLoading(false);

        if (res.success) {
            await refresh();
            navigate('/available-badges');
        } else {
            setErrors(res.errors ?? [res.message ?? 'Registration failed']);
        }
    };

    return (
        <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" bgcolor="background.default">
            <Card sx={{ width: 420, border: 'none' }}>
                <CardContent sx={{ p: 4 }}>
                    <Box textAlign="center" mb={3}>
                        <WorkspacePremiumIcon sx={{ fontSize: 48, color: 'accent.main' }} />
                        <Typography variant="h5" fontWeight={700} mt={1}>Create Account</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Join TC Everywhere today</Typography>
                    </Box>

                    {errors.length > 0 && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {errors.map((err, i) => <div key={i}>{err}</div>)}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={2}>
                        <Grid container spacing={1.5}>
                            <Grid item xs={6}>
                                <TextField
                                    id="firstName"
                                    label="First Name"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    required
                                    autoComplete="given-name"
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    id="lastName"
                                    label="Last Name"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    required
                                    autoComplete="family-name"
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                        </Grid>

                        <TextField
                            id="email"
                            label="Email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoComplete="email"
                            size="small"
                            fullWidth
                        />

                        <Box>
                            <TextField
                                id="password"
                                label="Password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoComplete="new-password"
                                size="small"
                                fullWidth
                            />
                            {strength && (
                                <Box mt={0.5}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={strength.score}
                                        color={strength.color}
                                        sx={{ height: 6, borderRadius: 3 }}
                                    />
                                    <Typography variant="caption" color={`${strength.color}.main`}>{strength.label}</Typography>
                                </Box>
                            )}
                        </Box>

                        <TextField
                            id="confirmPassword"
                            label="Confirm Password"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            autoComplete="new-password"
                            size="small"
                            fullWidth
                            error={confirmMismatch}
                            helperText={confirmMismatch ? 'Passwords do not match' : ''}
                        />

                        <TextField
                            id="organizationName"
                            label="Organization (optional)"
                            value={organizationName}
                            onChange={(e) => setOrganizationName(e.target.value)}
                            autoComplete="organization"
                            size="small"
                            fullWidth
                        />

                        <Button type="submit" variant="contained" fullWidth disabled={loading}>
                            {loading ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
                            Create Account
                        </Button>
                    </Box>

                    <Typography variant="body2" textAlign="center" sx={{ color: 'text.secondary' }} mt={2}>
                        Already have an account?{' '}
                        <Box component={Link} to="/login" sx={{ color: 'accent.main', textDecoration: 'none' }}>Sign in</Box>
                    </Typography>
                </CardContent>
            </Card>
        </Box>
    );
}
